import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  EllipsisVerticalIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@dashboard/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import Dialog from '@dashboard/components/Dialog';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import EventsLog from '@dashboard/components/EventsLog/EventsLog';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import Link from '@/dashboard/components/Link';
import WebhookSigningSecret from './WebhookSigningSecret';

const getStatusColor = (success, httpStatus) => {
  if (success && httpStatus >= 200 && httpStatus < 300) {
    return 'text-green-600 dark:text-green-500';
  }
  return 'text-red-600 dark:text-red-400';
};

const formatResponseBody = responseBody => {
  if (!responseBody) return 'No response body';
  try {
    const parsed = JSON.parse(responseBody);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return responseBody;
  }
};

const formatHeaders = headers => {
  if (!headers || Object.keys(headers).length === 0) {
    return 'No headers received';
  }
  return JSON.stringify(headers, null, 2);
};

const WebhookActionsMenu = ({ webhook, onEdit, onDelete }) => {
  const { sellerId, token } = useAuth();
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const testWebhook = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/webhooks/${webhook.id}/test`, token, {
        method: 'POST',
      }),
    onSuccess: data => {
      setTestResult(data);
      setShowResultDialog(true);
      if (data.success) {
        toast({
          title: 'Webhook test successful',
          description: `Response received in ${data.responseTime}ms`,
          type: 'success',
        });
      } else {
        toast({
          title: 'Webhook test failed',
          description: data.error || 'The endpoint did not respond successfully',
          type: 'error',
        });
      }
    },
    onError: error => {
      toast({
        title: 'Failed to test webhook',
        description: error?.message || 'Unable to send test request',
        type: 'error',
      });
    },
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" aria-label="Webhook actions">
            <EllipsisVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => testWebhook.mutate()}>Send test event</DropdownMenuItem>

          {webhook.integrationId && (
            <DropdownMenuItem onSelect={() => setShowLogsDialog(true)}>View logs</DropdownMenuItem>
          )}

          <DropdownMenuItem onSelect={() => setShowSecretDialog(true)}>
            View signing secret
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={() => onEdit(webhook)}>Edit</DropdownMenuItem>

          <DropdownMenuItem variant="destructive" onSelect={() => onDelete(webhook)}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        title="Webhook test result"
        cancelButton={null}
        confirmButton={{
          label: 'Close',
          appearance: 'primary',
          onClick: () => setShowResultDialog(false),
        }}
        size="xl"
        open={showResultDialog}
        onCancel={() => setShowResultDialog(false)}
      >
        {testResult && (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-card p-4 rounded-lg text-sm">
              <div>
                <span className="font-medium text-gray-900 dark:text-foreground">Status:</span>
                <span
                  className={`ml-2 font-medium ${getStatusColor(
                    testResult.success,
                    testResult.httpStatus,
                  )}`}
                >
                  {testResult.success ? 'Success' : 'Failed'}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-muted-foreground">
                  HTTP Status:
                </span>
                <span
                  className={`ml-2 font-medium ${getStatusColor(
                    testResult.success,
                    testResult.httpStatus,
                  )}`}
                >
                  {testResult.httpStatus}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-muted-foreground">
                  Response Time:
                </span>
                <span className="ml-2">{testResult.responseTime}ms</span>
              </div>

              <div>
                <span className="font-medium text-gray-700 dark:text-muted-foreground">
                  Endpoint:
                </span>
                <span className="ml-2 text-gray-600 dark:text-muted-foreground break-all">
                  {webhook.url}
                </span>
              </div>
            </div>

            {testResult.error && (
              <Alert variant="destructive" className="my-4">
                <ExclamationCircleIcon />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  <code>{testResult.error}</code>
                </AlertDescription>
              </Alert>
            )}

            {testResult.responseBody && (
              <Alert variant="success" className="my-4">
                <CheckCircleIcon />
                <AlertTitle>Response body</AlertTitle>
                <AlertDescription>
                  <pre className="w-full bg-gray-100 dark:bg-muted max-h-64 overflow-auto p-3 border rounded-lg text-sm whitespace-pre-wrap break-all">
                    {formatResponseBody(testResult.responseBody)}
                  </pre>
                </AlertDescription>
              </Alert>
            )}

            {testResult.headers && Object.keys(testResult.headers).length > 0 && (
              <Alert variant="success" className="my-4">
                <CheckCircleIcon />
                <AlertTitle>Response headers</AlertTitle>
                <AlertDescription>
                  <pre className="w-full bg-gray-100 dark:bg-muted max-h-48 overflow-auto p-3 border rounded-lg text-sm whitespace-pre-wrap break-all">
                    {formatHeaders(testResult.headers)}
                  </pre>
                </AlertDescription>
              </Alert>
            )}

            <Alert variant="default" className="my-4">
              <InformationCircleIcon />
              <AlertTitle>About webhook testing</AlertTitle>
              <AlertDescription>
                This test sends a sample payload to your webhook endpoint. A successful test means
                your endpoint is reachable and responding correctly. For production webhooks, ensure
                your endpoint validates the webhook signature and handles the actual event data
                structure.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </Dialog>

      <Dialog
        title="Signing secret"
        cancelButton={null}
        confirmButton={{
          label: 'Close',
          appearance: 'primary',
          onClick: () => setShowSecretDialog(false),
        }}
        size="xl"
        open={showSecretDialog}
        onCancel={() => setShowSecretDialog(false)}
      >
        {showSecretDialog && (
          <div className="space-y-4">
            <p className="text-sm text-gray-900 dark:text-foreground">
              Use this secret to verify that webhook requests come from Checkout Page. You can view
              or rotate it here at any time. See the{' '}
              <Link target="_blank" to="/docs/api/v1/webhooks/signature">
                webhook documentation
              </Link>{' '}
              for how to validate the signature.
            </p>
            <WebhookSigningSecret webhookId={webhook.id} />
          </div>
        )}
      </Dialog>

      {webhook.integrationId && (
        <Dialog
          title="Integration logs"
          cancelButton={null}
          confirmButton={null}
          size="5xl"
          open={showLogsDialog}
          onCancel={() => setShowLogsDialog(false)}
        >
          <EventsLog integrationId={webhook.integrationId} hideResend={true} />
        </Dialog>
      )}
    </>
  );
};

export default WebhookActionsMenu;
